import React, { useEffect } from "react";
import styles from "./CaseDetails.module.css";
import OverviewSection from "@_components/sections/OverviewSection/OverviewSection";
import CaseActivitySection from "@_components/sections/CaseActivitySection/CaseActivitySection";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import { caseActions } from "@_actions/case.actions";
import { sidebarActions } from "@_actions/sidebar.actions";
import { catchError } from "@_utils/helpers";
import caseStates from "@_configs/caseStates.config";

function CaseDetails(props) {
  const sidebarReducer = useSelector((state) => state.sidebarReducer);
  const caseReducer = useSelector((state) => state.caseReducer);
  const selectedCaseData =
    caseReducer.case[sidebarReducer.selections[sidebarReducer.status]];
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const caseStatesArray = Object.keys(caseStates);

  /* Initializes and validates the case details view based on the current URL path. This runs once on mount.
  
  This effect has two main responsibilities:
  1. Ensures the sidebar status is set to "CASES" if not already valid
  2. Fetches case details when navigating to a specific case via URL
  
  @triggers
  When the URL path matches the pattern: /cases/:caseId
  Example: /cases/123 or /cases/ABC-456

  * @improvement
   * Dependig on how we refactor this component, we might need to add location.pathname to the dependency array so the effect responds to
   * in-app navigation without a full remount.
  */

  useEffect(() => {
    if (pathParts.length === 2 && pathParts[0] === "cases") {
      if (
        !sidebarReducer.status ||
        !caseStatesArray.some(
          (state) =>
            state.toLowerCase() === sidebarReducer.status.toLowerCase(),
        )
      ) {
        dispatch(sidebarActions.updateStatus("CASES"));
      }
      if (
        !caseStatesArray.some(
          (state) => state.toLowerCase() === pathParts[1].toLowerCase(),
        )
      ) {
        dispatch(caseActions.getCaseWithChargeDetails(pathParts[1]));
      }
    }
    // eslint-disable-next-line
  }, [dispatch]);

  /* Handles the result of fetching case details and updates the UI accordingly.
  
  This effect runs AFTER the previous effect fetches case data via the API.
  It processes the API response and either:
  - Updates sidebar selections if case was found successfully
  - Navigates to 404 page if case doesn't exist
  - Clears loading/error messages in both cases

  @triggers
  When caseReducer.status.CASE_GET changes (API call completes)
  Must be on URL path: /cases/:caseId (checked via pathParts)

  @issue
   * If caseReducer.case is empty or the fetched case isn't found by case_id,
   * `routedCase` will be undefined and the sidebar selection won't be updated —
   * but no error is shown to the user.
  */
  useEffect(() => {
    if (pathParts.length === 2 && pathParts[0] === "cases") {
      if (caseReducer.status.CASE_GET === "success") {
        const routedCase = Object.values(caseReducer.case).find(
          (c) => c.case_id === pathParts[1],
        );
        if (routedCase) {
          dispatch(sidebarActions.updateSelections(routedCase.id));
        }
        dispatch(caseActions.clearStateMessage("CASE_GET"));
      } else if (catchError(caseReducer.status.CASE_GET)) {
        if (caseReducer.status.CASE_GET?.includes("Resource not found")) {
          history.push("/page_not_found");
        }
        dispatch(caseActions.clearStateMessage("CASE_GET"));
      }
    }
    // eslint-disable-next-line
  }, [dispatch, caseReducer.status.CASE_GET]);

  return (
    <div className={styles.root}>
      {selectedCaseData ? (
        <div style={{ marginRight: 20 }}>
          <div className={styles.top_container}>
            <OverviewSection data={selectedCaseData} />
          </div>
          <div className={styles.section_container}>
            <CaseActivitySection data={selectedCaseData} />
          </div>
        </div>
      ) : (
        <div className={styles.no_data}>No Case is Selected!</div>
      )}
    </div>
  );
}

export default CaseDetails;
